import React, { useEffect } from "react";
import { Box, Loader, LoadingOverlay, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { Title } from "../../stories/Title/Title";
import { UserType, UserRole, patientSexChoices } from "../../types/enums";
import SelectInputRef from "../SelectInputRef";
import { User, Order } from "../../types/interfaces";
import { useQuery } from "@apollo/client";
import {
  GET_ALL_PRACTITIONERS,
  GET_PRACTITIONERS_FROM_USER_GROUP,
} from "../../gql/users";
import { Controller, useFormContext } from "react-hook-form";
import { getProductsOrderedCountLabel } from "../../utils/product.utils";
import { useReloadOnNewAppVersion } from "@jasper/use-cases";

const parseMantineDateValue = (value: string | Date | null | undefined) => {
  if (!value) {
    return null;
  }

  return new Date(value);
};

export const Patient: React.FC<{
  order: Order;
  user: User;
}> = ({ order, user }) => {
  const { data: getPractitioners, loading: getPractitionersLoading } = useQuery(
    GET_PRACTITIONERS_FROM_USER_GROUP,
    {
      fetchPolicy: "network-only",
    }
  );

  const { loading: loadingAppVersion } = useReloadOnNewAppVersion();

  const { data: getAllDentist, loading: getAllDentistLoading } = useQuery(
    GET_ALL_PRACTITIONERS,
    {
      variables: {
        where: {
          role: {
            in: [UserRole.USER, UserRole.ADMIN, UserRole.STAFF],
          },
          type: {
            in: [UserType.DENTIST, UserType.JASPER],
          },
        },
      },
      fetchPolicy: "network-only",
      skip: user.type === UserType.DENTIST,
    }
  );

  const getPractitionersOptions = () => {
    return user?.role === UserRole.ADMIN
      ? (getAllDentist?.getManyUsers ?? [])
          .filter(usr =>
            (usr?.userGroup?.groupMembers ?? []).length > 1
              ? !usr?.isManager
              : true
          )
          .map(usr => ({
            label: `Dr. ${usr.firstName} ${usr.lastName} - ${usr.userGroup?.clinicNumber}`,
            value: usr.id,
          }))
      : ((getPractitioners?.getManagedUserGroupOfUser?.groupMembers ?? [])
          .filter(usr =>
            (getPractitioners?.getManagedUserGroupOfUser?.groupMembers ?? [])
              .length > 1
              ? !usr?.isManager
              : true
          )
          ?.map(groupMember => ({
            label: `Dr. ${groupMember.firstName} ${groupMember.lastName}`,
            value: groupMember.id,
          })) ?? []);
  };

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  useEffect(() => {
    if (order?.patient && order?.user) {
      setValue("firstName", order.patient.firstName);
      setValue("lastName", order.patient.lastName);
      setValue(
        "birthDate",
        order.patient.birthdate ? new Date(order.patient.birthdate) : null
      );
      setValue("sex", order.patient.sex);
      if (order.user.id) {
        setValue("doctorId", order.user.id);
      }
    }
  }, [order]);

  const getDefaultDoctor = (data: User[] | null): string | null => {
    return data?.length === 1 ? data[0].id : null;
  };

  const getMemberOfuserGroupWithoutMasterAccount = (
    groupMembers: User[] | null
  ): User[] => {
    return (groupMembers ?? []).filter(
      (groupMember: User) => !groupMember.isManager
    );
  };

  useEffect(() => {
    const members =
      getPractitioners?.getManagedUserGroupOfUser?.groupMembers ?? [];
    const membersWithoutMasterAccount =
      getMemberOfuserGroupWithoutMasterAccount(members);
    const defaultDoctor = getDefaultDoctor(
      membersWithoutMasterAccount.length === 1
        ? membersWithoutMasterAccount
        : members
    );
    if (defaultDoctor) {
      setValue("doctorId", defaultDoctor);
    }
  }, [
    JSON.stringify(
      getPractitioners?.getManagedUserGroupOfUser?.groupMembers ?? []
    ),
  ]);

  return (
    <Box pt="lg">
      <LoadingOverlay
        visible={
          getPractitionersLoading || getAllDentistLoading || loadingAppVersion
        }
      >
        <Loader />
      </LoadingOverlay>
      <Title variant="primary" size={4} title="Produit commandé" />
      <Text>{getProductsOrderedCountLabel(order)}</Text>
      <Box my="md">
        <Title
          variant="primary"
          size={4}
          title="Cette commande est réalisée par :"
        />
        <SelectInputRef
          label="Docteur"
          name="doctorId"
          data={getPractitionersOptions()}
          errors={errors}
          control={control}
          required
          errorMessage="Le docteur est requis"
          searchable
          clearable
          placeholder="Selectionnez un docteur"
          dataTestId="select-praticien"
        />
      </Box>
      <Title mb="sm" size={4} title="Patient :" />
      <TextInput
        {...register("firstName", { required: "Le prénom est requis" })}
        label="Prénom"
        placeholder="Prénom"
        size="sm"
        data-testid="input-patient-firstname"
      />
      {errors.firstName && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.firstName.message as string}
        </span>
      )}
      <TextInput
        {...register("lastName", { required: "Le nom est requis" })}
        label="Nom"
        placeholder="Nom"
        size="sm"
        data-testid="input-patient-lastname"
      />
      {errors.lastName && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.lastName.message as string}
        </span>
      )}
      <Controller
        control={control}
        name="birthDate"
        render={({ field }) => {
          return (
            <DateInput
              {...field}
              value={field.value ?? null}
              onChange={value => field.onChange(parseMantineDateValue(value))}
              placeholder="Date de naissance du patient"
              valueFormat="DD/MM/YYYY"
              label="Date de naissance"
              locale="fr"
              data-testid="input-patient-birthdate"
            />
          );
        }}
      />
      <SelectInputRef
        required={false}
        errors={errors}
        label="Sexe"
        name="sex"
        data={patientSexChoices}
        control={control}
        placeholder="Sexe du patient"
      />
    </Box>
  );
};
